import { useState, useEffect, useRef } from "react";
import { IoExpand, IoSettingsOutline } from "react-icons/io5";
import { BiHome, BiUser, BiTrash, BiRefresh } from "react-icons/bi";
import { TbCircles } from "react-icons/tb";
import { LuLoader } from "react-icons/lu";
import { RiAddLargeFill } from "react-icons/ri";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaDice } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "../style/PromptForm.css";
import IndexingIndicator from "./IndexingIndicator";

const Navbar = ({
    isHome = false,
    isFetching = false,
    embeddingProgress = null,
    setShowLoginModal,
    setShowSettingsModal,
    handleRemoveGraph,
    handleRebuildGraph,
    handleRandomNodeSelection,
    handleAddNewNode = null,
    sendNewPrompt,
    newPrompt,
    setNewPrompt,
    isNodeListOpen = false,
    handleNodeListToggle,
    handleZoomOut
}) => {
    const { t } = useTranslation();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const hasMenuItems = setShowSettingsModal || handleRandomNodeSelection || handleRemoveGraph || handleRebuildGraph;

    useEffect(() => {
        if (!menuOpen) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [menuOpen]);

    return <div className={`navbar ${isNodeListOpen ? 'sidebar-open' : ''}`}>
        {/* Horizontal row (X-axis: left to right) */}
        <div className="navbar-row-x">
            {!isHome ?
                <button title={t('navbar.goHome')} onClick={() => window.location.href = '/'} className='navbar-item icon'>
                    {<BiHome />}
                </button>
                :
                null}

            {setShowLoginModal ?
                <button title={t('navbar.yourAccount')} onClick={setShowLoginModal} className='navbar-item icon'>
                    {<BiUser />}
                </button>
                : null}

            {isFetching ? <button className='navbar-item icon blink_me rotating disabled'>
                <LuLoader />
            </button> : null}

            {handleZoomOut ?
                <button
                    onClick={handleZoomOut}
                    className="navbar-item icon"
                    title={t('navbar.fitAllNodes')}
                >
                    <IoExpand />
                </button>
                :
                null}

            {hasMenuItems ?
                <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                        onClick={() => setMenuOpen(prev => !prev)}
                        className="navbar-item icon"
                        title={t('navbar.more')}
                    >
                        <BsThreeDotsVertical />
                    </button>
                    {menuOpen &&
                        <div className="navbar-dropdown">
                            {setShowSettingsModal &&
                                <div className="navbar-dropdown-item" onClick={() => { setShowSettingsModal(); setMenuOpen(false); }}>
                                    <IoSettingsOutline style={{ marginRight: 8 }} />
                                    {t('navbar.settings')}
                                </div>
                            }
                            {handleRandomNodeSelection &&
                                <div className="navbar-dropdown-item" onClick={() => { handleRandomNodeSelection(); setMenuOpen(false); }}>
                                    <FaDice style={{ marginRight: 8 }} />
                                    {t('navbar.selectRandomNode')}
                                </div>
                            }
                            {handleRebuildGraph &&
                                <div className="navbar-dropdown-item" onClick={() => { handleRebuildGraph(); setMenuOpen(false); }}>
                                    <BiRefresh style={{ marginRight: 8 }} />
                                    {t('navbar.rebuildGraph')}
                                </div>
                            }
                            {handleRemoveGraph &&
                                <div className="navbar-dropdown-item" onClick={() => { handleRemoveGraph(); setMenuOpen(false); }}>
                                    <BiTrash style={{ marginRight: 8 }} />
                                    {t('navbar.deleteGraph')}
                                </div>
                            }
                        </div>
                    }
                </div>
                : null}

            <IndexingIndicator embeddingProgress={embeddingProgress} />
        </div>

        {/* Vertical row (Y-axis: top to bottom) */}
        <div className="navbar-row-y">
            {handleNodeListToggle ?
                <button
                    title={isNodeListOpen ? t('navbar.closeNodeList') : t('navbar.openNodeList')}
                    onClick={handleNodeListToggle}
                    className='navbar-item icon'
                >
                    <TbCircles />
                </button>
                : null}

            {handleAddNewNode ?
                <button
                    onClick={handleAddNewNode}
                    className="navbar-item icon"
                    title={t('navbar.createNewNode')}
                >
                    <RiAddLargeFill />
                </button>
                :
                null}
        </div>
    </div>;
}

export default Navbar